import React, { useState, useEffect } from 'react';
import { FileText, Plus, Eye, Play, Check, X, Search, Copy, Download, Edit2 } from 'lucide-react';
import { API_URL } from '../../services/api';
import CreateDevis from './CreateDevis';
import DevisDetails from './DevisDetails';
import EditDevisModal from './EditDevisModal';

const DevisPage = () => {
  const [devisList, setDevisList] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDevisId, setSelectedDevisId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [devisForProduction, setDevisForProduction] = useState(null);
  const [selectedPierres, setSelectedPierres] = useState([]);
  const [additionalPierres, setAdditionalPierres] = useState([]);
  const [editDevis, setEditDevis] = useState(null);

  useEffect(() => {
    loadDevis();
  }, []);

  const loadDevis = async () => {
    try {
      const response = await fetch(`${API_URL}/devis`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setDevisList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
    } finally {
      setLoading(false);
    }
  };

  const openProductionModal = async (devisId) => {
    try {
      // Charger les détails du devis avec les pierres
      const response = await fetch(`${API_URL}/devis/${devisId}`);
      const devis = await response.json();
      
      setDevisForProduction(devis);
      // Pré-sélectionner toutes les pierres du devis
      setSelectedPierres(devis.pierres?.map(p => p.id) || []);
      setAdditionalPierres([]);
      setShowProductionModal(true);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
      alert('Erreur lors du chargement du devis');
    }
  };

  const lancerProductionDirect = async (devisId, statut) => {
    if (!window.confirm('Lancer la production pour ce devis ?')) return;
    try {
      // Si EN_ATTENTE, valider d'abord automatiquement
      if (statut === 'EN_ATTENTE') {
        await fetch(`${API_URL}/devis/${devisId}/valider`, { method: 'POST' });
      }
      const response = await fetch(`${API_URL}/devis/${devisId}/lancer-production`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (response.ok) {
        loadDevis();
      } else {
        const err = await response.json();
        alert(err.error || 'Erreur lors du lancement de la production');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du lancement de la production');
    }
  };

  const lancerProduction = async () => {
    try {
      const response = await fetch(`${API_URL}/devis/${devisForProduction.id}/lancer-production`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pierres_selectionnees: selectedPierres,
          pierres_additionnelles: additionalPierres
        })
      });

      if (response.ok) {
        setShowProductionModal(false);
        loadDevis();
      } else {
        alert('Erreur lors du lancement de la production');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du lancement de la production');
    }
  };

  const togglePierre = (pierreId) => {
    setSelectedPierres(prev => 
      prev.includes(pierreId) 
        ? prev.filter(id => id !== pierreId)
        : [...prev, pierreId]
    );
  };

  const dupliquerDevis = async (devisId) => {
    if (!window.confirm('Voulez-vous créer un nouveau devis identique à celui-ci ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/devis/${devisId}/dupliquer`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Devis dupliqué avec succès ! Nouvelle référence: ${data.reference_produit}`);
        loadDevis();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la duplication');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la duplication du devis');
    }
  };

  const filteredDevis = devisList.filter(devis => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (devis.reference_modele || '').toLowerCase().includes(search) ||
      (devis.reference_produit || '').toLowerCase().includes(search) ||
      (devis.numero_devis || '').toLowerCase().includes(search) ||
      (devis.type_bijou || '').toLowerCase().includes(search) ||
      (devis.client_nom || devis.nom_client || '').toLowerCase().includes(search) ||
      (devis.collection_nom || devis.nom_collection || '').toLowerCase().includes(search) ||
      (devis.description || '').toLowerCase().includes(search)
    );
  });

  const getStatutBadge = (statut) => {
    const badges = {
      'EN_ATTENTE': 'bg-yellow-100 text-yellow-800',
      'VALIDE': 'bg-green-100 text-green-800',
      'EN_PRODUCTION': 'bg-blue-100 text-blue-800',
      'TERMINE': 'bg-purple-100 text-purple-800',
      'ANNULE': 'bg-red-100 text-red-800'
    };
    return badges[statut] || 'bg-gray-100 text-gray-800';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'EN_ATTENTE': 'En attente',
      'VALIDE': 'Validé',
      'EN_PRODUCTION': 'En production',
      'TERMINE': 'Terminé',
      'ANNULE': 'Annulé'
    };
    return labels[statut] || statut;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center space-x-3">
              <FileText className="h-8 w-8 text-blue-600" />
              <span>Gestion des Devis</span>
            </h1>
            <p className="text-gray-600 mt-1">Créez vos devis avant de lancer la production</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center space-x-2 shadow-lg"
          >
            <Plus className="h-5 w-5" />
            <span>Nouveau Devis</span>
          </button>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center space-x-3">
            <Search className="h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par référence, type, client, collection..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-600 mt-2">
              {filteredDevis.length} résultat{filteredDevis.length > 1 ? 's' : ''} trouvé{filteredDevis.length > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-600">En attente</p>
            <p className="text-2xl font-bold text-yellow-600">
              {devisList.filter(d => d.statut === 'EN_ATTENTE').length}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-600">Validés</p>
            <p className="text-2xl font-bold text-green-600">
              {devisList.filter(d => d.statut === 'VALIDE').length}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-600">En production</p>
            <p className="text-2xl font-bold text-blue-600">
              {devisList.filter(d => d.statut === 'EN_PRODUCTION').length}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-600">Terminés</p>
            <p className="text-2xl font-bold text-purple-600">
              {devisList.filter(d => d.statut === 'TERMINE').length}
            </p>
          </div>
        </div>

        {/* Liste des devis — mobile cards */}
        <div className="md:hidden space-y-3 mb-4">
          {filteredDevis.map((devis) => (
            <div key={devis.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-400">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-bold text-gray-900 font-mono text-sm">
                    {devis.reference_produit || devis.numero_devis || `#${devis.id}`}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {devis.type_creation === 'CLIENT' ? devis.client_nom : devis.collection_nom} · {devis.type_bijou}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {new Date(devis.date_creation).toLocaleDateString()}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatutBadge(devis.statut)}`}>
                  {getStatutLabel(devis.statut)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="font-bold text-green-600 text-sm">
                  {parseFloat(devis.prix_vente_final || 0).toFixed(2)} DH
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setSelectedDevisId(devis.id)}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs"
                  >
                    Voir
                  </button>
                  {(devis.statut === 'EN_ATTENTE' || devis.statut === 'VALIDE') && (
                    <button
                      onClick={() => setEditDevis(devis)}
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs flex items-center gap-1"
                    >
                      <Edit2 size={12} /> Modifier
                    </button>
                  )}
                  {(devis.statut === 'EN_ATTENTE' || devis.statut === 'VALIDE') && (
                    <button
                      onClick={() => lancerProductionDirect(devis.id, devis.statut)}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs"
                    >
                      Lancer prod
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {filteredDevis.length === 0 && (
            <div className="text-center py-8 text-gray-400">Aucun devis trouvé</div>
          )}
        </div>

        {/* Liste des devis — desktop table */}
        <div className="hidden md:block bg-white rounded-lg shadow-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '900px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client/Collection</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bijou</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Couleur Or</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Vente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap" style={{ minWidth: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDevis.map((devis) => (
                <tr key={devis.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-mono text-sm font-bold text-indigo-600">
                      {devis.reference_produit || devis.numero_devis || `#${devis.id}`}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {new Date(devis.date_creation).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      devis.type_creation === 'BIJOUTERIE' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {devis.type_creation}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {devis.type_creation === 'CLIENT' ? devis.client_nom : devis.collection_nom}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {devis.type_bijou}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      devis.couleur_or === 'JAUNE' ? 'bg-yellow-100 text-yellow-800' :
                      devis.couleur_or === 'BLANC' ? 'bg-gray-100 text-gray-800' :
                      'bg-pink-100 text-pink-800'
                    }`}>
                      {devis.couleur_or}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-green-600">
                    {parseFloat(devis.prix_vente_final || 0).toFixed(2)} DH
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatutBadge(devis.statut)}`}>
                      {getStatutLabel(devis.statut)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedDevisId(devis.id)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                        title="Voir détails"
                      >
                        <Eye size={18} />
                      </button>
                      {(devis.statut === 'EN_ATTENTE' || devis.statut === 'VALIDE') && (
                        <button
                          onClick={() => setEditDevis(devis)}
                          className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-100"
                          title="Modifier le devis"
                        >
                          <Edit2 size={18} />
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          const { generateDevisPDF } = await import('../../utils/pdfGenerator');
                          await generateDevisPDF(devis);
                        }}
                        className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50"
                        title="Télécharger PDF"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => dupliquerDevis(devis.id)}
                        className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50"
                        title="Dupliquer ce devis"
                      >
                        <Copy size={18} />
                      </button>
                      {(devis.statut === 'EN_ATTENTE' || devis.statut === 'VALIDE') && (
                        <button
                          onClick={() => lancerProductionDirect(devis.id, devis.statut)}
                          className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                          title="Lancer production"
                        >
                          <Play size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal Créer Devis */}
        {showCreateModal && (
          <CreateDevis
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              loadDevis();
            }}
          />
        )}

        {/* Modal Détails Devis */}
        {selectedDevisId && (
          <DevisDetails
            devisId={selectedDevisId}
            onClose={() => setSelectedDevisId(null)}
            onValidated={() => {
              setSelectedDevisId(null);
              loadDevis();
            }}
          />
        )}

        {/* Modal Lancement Production Personnalisé */}
        {showProductionModal && devisForProduction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold mb-4 text-gray-800">
                🚀 Lancer la Production
              </h3>
              
              <div className="mb-6 bg-blue-50 p-4 rounded-lg">
                <p className="font-semibold text-blue-900">
                  {devisForProduction.type_bijou} - {devisForProduction.couleur_or}
                </p>
                <p className="text-sm text-blue-700">
                  Référence: {devisForProduction.reference_produit}
                </p>
              </div>

              {/* Sélection des pierres */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-3 flex items-center">
                  💎 Pierres prévues dans le devis
                  <span className="ml-2 text-sm text-gray-500">(Cochez celles à utiliser)</span>
                </h4>
                
                {devisForProduction.pierres && devisForProduction.pierres.length > 0 ? (
                  <div className="space-y-2">
                    {devisForProduction.pierres.map((pierre) => (
                      <label 
                        key={pierre.id}
                        className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPierres.includes(pierre.id)}
                          onChange={() => togglePierre(pierre.id)}
                          className="w-5 h-5 text-blue-600 rounded mr-3"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{pierre.type_pierre}</p>
                          <p className="text-sm text-gray-600">
                            Quantité: {pierre.quantite} | Prix: {parseFloat(pierre.prix_unitaire || 0).toFixed(2)} DH
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">Aucune pierre prévue dans ce devis</p>
                )}
              </div>

              {/* Pierres additionnelles */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-3">
                  ➕ Ajouter d'autres pierres (optionnel)
                </h4>
                <button
                  onClick={() => setAdditionalPierres([...additionalPierres, { type: '', quantite: 0 }])}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  + Ajouter une pierre
                </button>
                
                {additionalPierres.map((pierre, idx) => (
                  <div key={idx} className="flex gap-3 mt-3">
                    <input
                      type="text"
                      placeholder="Type de pierre (ex: Rubis)"
                      value={pierre.type}
                      onChange={(e) => {
                        const updated = [...additionalPierres];
                        updated[idx].type = e.target.value;
                        setAdditionalPierres(updated);
                      }}
                      className="flex-1 px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      placeholder="Quantité"
                      value={pierre.quantite}
                      onChange={(e) => {
                        const updated = [...additionalPierres];
                        updated[idx].quantite = parseFloat(e.target.value) || 0;
                        setAdditionalPierres(updated);
                      }}
                      className="w-32 px-3 py-2 border rounded-lg"
                    />
                    <button
                      onClick={() => setAdditionalPierres(additionalPierres.filter((_, i) => i !== idx))}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Boutons d'action */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowProductionModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={lancerProduction}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                >
                  <Play size={18} />
                  <span>Lancer la Production</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal modification devis */}
      {editDevis && (
        <EditDevisModal
          devis={editDevis}
          onClose={() => setEditDevis(null)}
          onSaved={(updated) => {
            setDevisList(prev => prev.map(d => d.id === (updated?.id || editDevis.id) ? { ...d, ...updated } : d));
            setEditDevis(null);
          }}
        />
      )}
    </div>
  );
};

export default DevisPage;
